import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, Drawer, Stack } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useMenu } from "@shared/hooks/SectionHook";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import Icon from "@shared/components/Icon";
import NavigationItem from "./NavigationItem";
import generateTemplatePropsFromContents from "@frontoffice/utils/templates/generateTemplatePropsFromContents";
import { useSites } from "@shared/hooks/SiteHook";
import { useHomePageLocation } from "@administration/hooks/PageHook";
import LinkEditor from "@administration/components/editor/LinkEditor";
import Link from "@shared/components/contents/link/Link";
import useGlobalStore from "@shared/store/GlobalStore";
const Navigation = () => {
    const location = useLocation();
    const [menuMobileActive, setMenuMobileActive] = useState(false);
    const theme = useTheme();
    // On passe en menu mobile tôt car le menu avec ses actions peut être trop large pour les écrans moyens
    const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
    const { currentSite: site } = useSites();
    const { settings: siteSettings } = site || {};
    const { logoHeader = null } = siteSettings || {};
    const { isBo } = useGlobalStore((state) => ({ isBo: state.isBo }));
    const { homePage } = useHomePageLocation();
    const { menu } = useMenu(site?.id);
    useEffect(() => {
        if (!isMobile && menuMobileActive)
            setMenuMobileActive(false);
    }, [isMobile, menuMobileActive, location]);
    const menuItems = useMemo(() => {
        const menuContents = (menu &&
            generateTemplatePropsFromContents(menu.contents)) || { menus: [] };
        if (menuContents?.menus && !Array.isArray(menuContents?.menus)) {
            return [menuContents?.menus];
        }
        return menuContents?.menus || [];
    }, [menu]);
    const MenuAction = useMemo(() => menuItems?.map((menuItem) => {
        const isIcon = !!menuItem &&
            !!menuItem?.["icon"] &&
            menuItem?.["icon"]["icon"] !== "";
        if (menuItem?.["type"] === "MENU_ACTION_BUTTON") {
            return (_jsx(Button, { ...menuItem, component: Link, ...menuItem.link, ...(isIcon && {
                    startIcon: (_jsx(Icon, { ...menuItem?.["icon"], color: "currentColor", style: {
                            color: "currentColor",
                            fontSize: 16,
                            width: "16px",
                            height: "16px",
                        } })),
                }), children: menuItem.title }, menuItem?.id));
        }
        return (_jsx(NavigationItem, { menuItem: menuItem, depth: 0 }, menuItem?.id));
    }), [menuItems]);
    if (isMobile) {
        return (_jsxs(_Fragment, { children: [_jsx("nav", { children: _jsx(IconButton, { "aria-label": "Bouton d'ouverture du menu mobile", size: "large", onClick: () => setMenuMobileActive(true), sx: {
                            color: "text.primary",
                        }, children: _jsx("i", { className: "fal fa-bars" }) }) }), _jsxs(Drawer, { open: menuMobileActive, onClose: () => setMenuMobileActive(false), anchor: "right", slotProps: {
                        paper: {
                            sx: {
                                backgroundColor: "background.paper",
                                minWidth: "300px",
                                maxWidth: "100%",
                            },
                        },
                    }, children: [_jsxs(Stack, { direction: "row", component: "nav", sx: {
                                width: "100%",
                                alignItems: "center",
                                justifyContent: "space-between",
                                px: 2,
                                py: 1,
                                "& img": {
                                    width: "160px",
                                },
                            }, children: [isBo ? (_jsx(LinkEditor, { page: homePage, onClick: () => setMenuMobileActive(false), children: logoHeader || _jsx("img", { alt: "", src: "/logo-docaposte-blue.svg" }) })) : (_jsx(Link, { url: "/", onClick: () => setMenuMobileActive(false), children: logoHeader || _jsx("img", { alt: "", src: "/logo-docaposte-blue.svg" }) })), _jsx(IconButton, { "aria-label": "Bouton de fermeture du menu mobile", onClick: () => setMenuMobileActive(false), size: "medium", sx: { color: "text.primary", my: 2, width: 40, height: 40 }, children: _jsx("i", { className: "fal fa-times" }) })] }), _jsx(Stack, { component: "nav", spacing: 3.5, onClick: (event) => {
                                const target = event.target;
                                if (target?.closest("a")) {
                                    setMenuMobileActive(false);
                                }
                            }, sx: {
                                "& > :not(.MuiButton-root), & > :not(.MuiButton-root) .MuiTypography-root, & > :not(.MuiButton-root) i": {
                                    color: (theme) => `${theme.palette.text.primary} !important`,
                                },
                                alignItems: "flex-start",
                                mt: 3,
                                px: 2,
                            }, children: MenuAction })] })] }));
    }
    return (_jsx(Stack, { component: "nav", "aria-label": "Navigation principale", direction: "row", spacing: 4, sx: {
            alignItems: "center",
            "& a, & button, & .MuiTypography-root": {
                "@media (min-width:1200px)": {
                    fontSize: "14px !important",
                },
                "@media (min-width:1440px)": {
                    fontSize: "15px !important",
                },
                fontSize: "15px !important",
            },
            "& > :not(.MuiButton-root), & > :not(.MuiButton-root) .MuiTypography-root, & > :not(.MuiButton-root) i": {
                color: (theme) => `${theme.palette.text.primary} !important`,
                "[data-dark] &": {
                    color: (theme) => `${theme.palette.primary.contrastText} !important`,
                },
            },
        }, children: MenuAction }));
};
export default Navigation;
